import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productSlug } from '../../hooks/useProducts';
import { useDevices } from '../../contexts/DeviceContext';
import { normalizeText } from '../../utils/faq/searchUtils';
import { Note, RichText, rich } from '../ui';
import { CheckIcon, ChevronRightIcon, CloseIcon, SearchIcon } from '../ui/Icons';
import { Link } from 'react-router-dom';
import { isWatchfaceProduct } from '../../utils/productType';
import { overviewIcons } from '../../data/features/overviewIcons';
import { AlertIcon, AodIcon, BookIcon, CardsIcon, ChartIcon, CloudIcon, DataSetIcon, GaugeIcon, GlassIcon, GlobeIcon, MoonPhaseIcon, PhoneLinkIcon, SettingsIcon, ShipIcon, StarIcon, SunEventIcon, SunMoonArcIcon, SwitchviewIcon, ThemesIcon, TidesIcon, TouchIcon, TrophyIcon, WeatherIcon, } from '../ui/GuideIcons';
const FEATURE_ICONS = {
    air: CloudIcon,
    alert: AlertIcon,
    aod: AodIcon,
    book: BookIcon,
    cards: CardsIcon,
    chart: ChartIcon,
    data: DataSetIcon,
    gauge: GaugeIcon,
    glass: GlassIcon,
    globe: GlobeIcon,
    moon: MoonPhaseIcon,
    phone: PhoneLinkIcon,
    settings: SettingsIcon,
    ship: ShipIcon,
    star: StarIcon,
    sunEvent: SunEventIcon,
    sunMoon: SunMoonArcIcon,
    switch: SwitchviewIcon,
    themes: ThemesIcon,
    tides: TidesIcon,
    touch: TouchIcon,
    trophy: TrophyIcon,
    weather: WeatherIcon,
};
export function useOverview(product) {
    const { t } = useTranslation();
    const data = t(`products.${productSlug(product)}`, { returnObjects: true, defaultValue: null });
    return data && typeof data === 'object' ? data : null;
}
/** „Läuft es auf meiner Uhr?“ – Suche in der Liste der kompatiblen Geräte */
const DeviceCheck = ({ product }) => {
    const { t } = useTranslation();
    const { available, devices, byId, selected } = useDevices();
    const [query, setQuery] = useState('');
    const [showAll, setShowAll] = useState(false);
    const ids = product.compatibleDeviceIds;
    const supported = useMemo(() => (ids || []).map((id) => byId.get(id)).filter(Boolean).map((d) => d).sort((a, b) => a.name.localeCompare(b.name)), [ids, byId]);
    const matches = useMemo(() => {
        const q = normalizeText(query.trim());
        if (!q)
            return [];
        const terms = q.split(/\s+/);
        return devices.filter((d) => terms.every((term) => normalizeText(d.name).includes(term))).slice(0, 8);
    }, [devices, query]);
    if (!available || !ids || ids.length === 0)
        return null;
    const idSet = new Set(ids);
    return (_jsxs("section", { className: "device-check", "aria-labelledby": "device-check-title", children: [_jsx("h3", { id: "device-check-title", children: t('pages.devices.checkTitle') }), selected && (_jsxs("p", { className: `device-check-mine ${idSet.has(selected.id) ? 'ok' : 'no'}`, children: [idSet.has(selected.id) ? _jsx(CheckIcon, { size: 18 }) : _jsx(CloseIcon, { size: 18 }), t(idSet.has(selected.id) ? 'pages.devices.compatible' : 'pages.devices.notCompatible', {
                        device: selected.name,
                    })] })), _jsxs("div", { className: "device-search", children: [_jsx(SearchIcon, { size: 18 }), _jsx("input", { id: "device-check-search", type: "search", value: query, onChange: (e) => setQuery(e.target.value), placeholder: t('pages.devices.checkPlaceholder'), "aria-label": t('pages.devices.checkPlaceholder'), autoComplete: "off" })] }), matches.length > 0 && (_jsx("ul", { className: "device-check-results", children: matches.map((d) => (_jsxs("li", { className: idSet.has(d.id) ? 'ok' : 'no', children: [idSet.has(d.id) ? _jsx(CheckIcon, { size: 16 }) : _jsx(CloseIcon, { size: 16 }), _jsx("span", { children: d.name }), _jsx("span", { className: "device-check-state", children: idSet.has(d.id) ? t('pages.devices.supported') : t('pages.devices.notSupported') })] }, d.id))) })), _jsxs("details", { className: "device-check-all", open: showAll, onToggle: (e) => setShowAll(e.target.open), children: [_jsx("summary", { children: t('pages.devices.listTitle', { count: supported.length }) }), showAll && (_jsx("ul", { className: "device-check-list", children: supported.map((d) => (_jsx("li", { children: d.name }, d.id))) }))] })] }));
};
const OverviewTab = ({ product }) => {
    const { t } = useTranslation();
    const overview = useOverview(product);
    return (_jsxs("div", { className: "product-overview", children: [_jsxs("div", { className: "product-overview-main", children: [overview?.description && _jsx(RichText, { text: overview.description, className: "product-description" }), overview?.features && overview.features.length > 0 && (_jsxs(_Fragment, { children: [_jsx("h3", { children: t('pages.product.featuresTitle') }), _jsx("ul", { className: "product-features", children: overview.features.map((f, i) => {
                                    const Icon = FEATURE_ICONS[overviewIcons[productSlug(product)]?.[i] || 'book'];
                                    return (_jsxs("li", { children: [_jsx("span", { className: "icon-tile frame feature-icon", children: _jsx(Icon, { size: 28 }) }), _jsx("span", { children: rich(f) })] }, i));
                                }) }), isWatchfaceProduct(product.is_vx) && (_jsxs(Link, { to: "/guides/data-fields", className: "product-datafields-link", children: [t('pages.product.allDataFields'), _jsx(ChevronRightIcon, { size: 16 })] }))] }))] }), _jsxs("aside", { className: "product-overview-side", children: [overview?.deviceNotes && overview.deviceNotes.length > 0 && (_jsx(Note, { label: t('pages.product.deviceNotesTitle'), children: _jsx("ul", { children: overview.deviceNotes.map((n, i) => (_jsx("li", { children: rich(n) }, i))) }) })), _jsx(DeviceCheck, { product: product })] })] }));
};
export default OverviewTab;
